import { parseISO } from "date-fns";
import {
    AccountClass,
    BalanceSheetDerivativeLines,
} from "@common/constants/domains/accounting/accounts";
import { ClassFilterType } from "@common/types/class";
import { JournalEntrySourceFilter } from "@common/schemas/domains/accounting/journalEntry";
import { Account } from "@common/types/domains/accounting/account";
import { ScheduleJeSubtype } from "@common/types/domains/accounting/schedule";
import { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import { AccountingReportFilters } from "./types";

export interface GlDrillDownOptions {
    startDate?: Date | string;
    endDate?: Date | string;
    entityId?: number;
    entityIds?: number[];
    accountCodes?: Array<number | string>;
    classes?: string[];
    classFilterType?: ClassFilterType;
    pnlBalanceSince?: Date;
    counterpartiesIds?: string[];
    withEmptyOrUnverifiedCounterparty?: boolean;
    hasCounterparty?: boolean;
    accountIds?: number[];
    subaccountIds?: string[];
    ledgerId?: string;
    journalEntrySources?: JournalEntrySourceFilter[];
    isAllTime?: boolean;
    transactionIds?: number[];
    scheduleApplicationIds?: string[];
    scheduleJeSubtypes?: ScheduleJeSubtype[];
}

export interface GlDrillDownParams {
    filters: AccountingReportFilters;
    entityIds: number[];
    classifications?: AccountClass[];
    pnlBalanceSince?: Date;
}

export interface GlTableCallbacks {
    onDrillDown?: (params: GlDrillDownParams) => void;
}

export type GlTableMeta = ViewTableMeta<GlTableCallbacks, unknown>;

export function toReportDateOnly(value?: Date | string): Date | undefined {
    if (!value) {
        return undefined;
    }
    return typeof value === "string" ? parseISO(value) : value;
}

export function resolveGlAccountCodes(accountCodes?: Array<number | string>): {
    accountCodes?: number[];
    classifications?: AccountClass[];
} {
    if (!accountCodes || accountCodes.length === 0) {
        return {};
    }

    const includesNetIncome = accountCodes.some(
        (code) => code === BalanceSheetDerivativeLines.netIncome,
    );
    const resolvedCodes = (
        includesNetIncome
            ? accountCodes.filter(
                  (code) => code !== BalanceSheetDerivativeLines.netIncome,
              )
            : accountCodes
    )
        .map((code) => Number(code))
        .filter((code) => Number.isFinite(code));

    return {
        accountCodes: resolvedCodes.length > 0 ? resolvedCodes : undefined,
        classifications: includesNetIncome
            ? [AccountClass.expenses, AccountClass.income]
            : undefined,
    };
}

interface ResolveClassificationAccountCodesParams {
    accountCodes?: number[];
    classifications?: AccountClass[];
    accounts: Array<Pick<Account, "code" | "class">>;
}

export function resolveClassificationAccountCodes({
    accountCodes,
    classifications,
    accounts,
}: ResolveClassificationAccountCodesParams): number[] | undefined {
    if (!classifications || classifications.length === 0) {
        return accountCodes;
    }
    const classificationCodes = accounts
        .filter((account) => classifications.includes(account.class))
        .map((account) => account.code);
    return [...new Set([...(accountCodes ?? []), ...classificationCodes])];
}

export function buildGlDrillDownParams({
    startDate,
    endDate,
    entityId,
    entityIds,
    accountCodes,
    classes,
    classFilterType,
    pnlBalanceSince,
    counterpartiesIds,
    withEmptyOrUnverifiedCounterparty,
    hasCounterparty,
    accountIds,
    subaccountIds,
    ledgerId,
    journalEntrySources,
    isAllTime,
    transactionIds,
    scheduleApplicationIds,
    scheduleJeSubtypes,
}: GlDrillDownOptions): GlDrillDownParams {
    const resolvedAccounts = resolveGlAccountCodes(accountCodes);
    let resolvedEntityIds: number[] = [];
    if (entityIds && entityIds.length > 0) {
        resolvedEntityIds = entityIds;
    } else if (entityId !== undefined) {
        resolvedEntityIds = [entityId];
    }

    return {
        filters: {
            startDate: toReportDateOnly(startDate),
            endDate: toReportDateOnly(endDate),
            entityId,
            entityIds,
            accountCodes: resolvedAccounts.accountCodes,
            classes,
            classFilterType,
            counterpartiesIds,
            withEmptyOrUnverifiedCounterparty,
            hasCounterparty,
            accountIds,
            subaccountIds,
            ledgerId,
            journalEntrySources,
            isAllTime,
            transactionIds,
            scheduleApplicationIds,
            scheduleJeSubtypes,
        },
        entityIds: resolvedEntityIds,
        classifications: resolvedAccounts.classifications,
        pnlBalanceSince,
    };
}
